import { ButtonLink, Modal } from '@edgebox/react-components';
import { ISyncCoreApi } from '@edgebox/sync-core-rest-client';
import type { DefaultApi } from '@edgebox/sync-core-rest-client/dist/services/api/apis';
import type { StartAnswerExportDto } from '@edgebox/sync-core-rest-client/dist/services/api/models';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { SyncCoreApiContext } from '../../../../contexts/SyncCoreApiContext';
import { EmbeddedModal } from '../../../EmbeddedModal';
import {
  ANSWER_EXPORT_COUNT_DEBOUNCE_MS,
  ANSWER_EXPORT_POLL_INTERVAL_MS,
  AnswerExportPhase,
  AnswerExportProgress,
  dateInputToEpochMs,
  epochMsToDateInput,
  exportCountButtonLabel,
  exportPhaseFromStatus,
  exportProgressFromSummary,
  normalizeStartDto,
  seedExportDto,
  toCountRequest,
} from './answerExport.utils';
import { PromptLabel } from '../GovernanceAnalyticsAiAnswers.utils';
import { ProgressBar } from './ProgressBar';
import { TermFilterOption } from './termStatusPresentation';
import { BP_PORTAL_CLASS_NAME, BP_PRIMARY_BUTTON_CLASSES } from './brandPresenceClasses';

/**
 * The export's start and count routes have no wrapper method on `ISyncCoreApi`, and the task-group
 * and file reads are needed with the generated entity shapes (the export details and the archive
 * reference). The provider hands over the configured client, whose underlying generated `DefaultApi`
 * is reached through this one documented cast rather than a hand-written call at a backend URL.
 */
function getDefaultApi(api: ISyncCoreApi): DefaultApi {
  return (api as unknown as { services: { api: DefaultApi } }).services.api;
}

export interface GovernanceAnswerExportModalProps {
  show: boolean;
  onHide: () => void;
  /** The list screen's current single-value filters, mirrored as the export's defaults. */
  pageFilters: { engineKey: string; localeKey: string; promptKey: string };
  engineOptions: readonly TermFilterOption[];
  localeOptions: readonly TermFilterOption[];
  promptOptions: readonly PromptLabel[];
}

/** The lifecycle of one export, from the click through to the archive. */
type ExportRunState =
  | { status: 'idle' }
  | { status: 'starting' }
  | { status: 'running'; groupId: string; phase: AnswerExportPhase; progress: AnswerExportProgress }
  | { status: 'ready'; groupId: string; downloadUrl?: string; resolving: boolean; resolveError?: string }
  | { status: 'failed'; message: string };

const START_FAILED_MESSAGE = 'The export could not be started. Please try again.';
const JOB_FAILED_MESSAGE = 'The export did not finish. No archive was produced; please try again.';
const ARCHIVE_MISSING_MESSAGE = 'The export finished but its archive is no longer available to download.';

export function GovernanceAnswerExportModal({
  show,
  onHide,
  pageFilters,
  engineOptions,
  localeOptions,
  promptOptions,
}: GovernanceAnswerExportModalProps) {
  const apiContext = useContext(SyncCoreApiContext);
  const api = apiContext?.api;

  const [dto, setDto] = useState<StartAnswerExportDto>(() => seedExportDto(pageFilters));
  const [count, setCount] = useState<number | undefined>(undefined);
  const [counting, setCounting] = useState(false);
  const [countError, setCountError] = useState(false);
  // The serialized count request the current `count` was taken for. When it no longer equals the
  // live request, the count is stale and the button must not offer it — this is what keeps X exact
  // across a filter edit without forcing a recount for edits that cannot change the count.
  const [countedKey, setCountedKey] = useState<string | undefined>(undefined);
  const [run, setRun] = useState<ExportRunState>({ status: 'idle' });

  // Mirrors `show` for the async lifecycle: a task-group tick or an archive lookup that resolves
  // after the modal is dismissed must not write state back into a closed modal.
  const showRef = useRef(show);
  showRef.current = show;

  // Re-seed on open so a second export starts from the list's current filters; on close, drop back to
  // idle, which tears down any running poll (the poll effect keys on the running group's id).
  useEffect(() => {
    if (show) {
      setDto(seedExportDto(pageFilters));
      setRun({ status: 'idle' });
      setCount(undefined);
      setCountedKey(undefined);
      setCountError(false);
    } else {
      setRun({ status: 'idle' });
    }
    // The page filters are captured at open; editing them behind the modal does not reach in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const running = run.status === 'starting' || run.status === 'running';

  // A single-value select mirrors the list screen; its choice is carried as a one-item list, since
  // the export filters on lists.
  const singleValue = (list?: string[] | null): string => (list && list.length ? String(list[0]) : '');
  const patch = useCallback((next: Partial<StartAnswerExportDto>) => {
    setDto((current) => ({ ...current, ...next }));
    // Editing filters after a finished or failed run starts a fresh one. The count itself is kept
    // honest by `countedKey` below, not by clearing it here — an include-flag edit does not change
    // the count and must not force a recount.
    setRun((current) => (current.status === 'ready' || current.status === 'failed' ? { status: 'idle' } : current));
  }, []);

  // Live count for the current filters, debounced so editing the window is one request. A stale
  // response never overwrites a newer one, and a failure clears the number rather than showing an
  // old one — the button never promises a count the archive would not match.
  const countRequestKey = useMemo(() => JSON.stringify(toCountRequest(dto)), [dto]);
  const countToken = useRef(0);
  useEffect(() => {
    if (!show || !api || running) {
      return;
    }
    const token = ++countToken.current;
    const key = countRequestKey;
    setCounting(true);
    setCountError(false);
    const timer = setTimeout(() => {
      void getDefaultApi(api)
        .monitoringPromptRunControllerCountAnswerExport(toCountRequest(dto))
        .then((response) => {
          if (countToken.current === token) {
            setCount(response.count);
            setCountedKey(key);
            setCounting(false);
          }
        })
        .catch(() => {
          if (countToken.current === token) {
            setCount(undefined);
            setCountedKey(key);
            setCountError(true);
            setCounting(false);
          }
        });
    }, ANSWER_EXPORT_COUNT_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // Keyed on the serialized count request so only a real filter change re-counts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, api, running, countRequestKey]);

  const resolveArchive = useCallback(
    async (groupId: string, archiveId: string | undefined) => {
      if (!api) {
        return;
      }
      if (!archiveId) {
        if (showRef.current) {
          setRun({ status: 'ready', groupId, resolving: false, resolveError: ARCHIVE_MISSING_MESSAGE });
        }
        return;
      }
      try {
        const file = await getDefaultApi(api).fileControllerItem({ id: archiveId });
        if (showRef.current) {
          setRun({ status: 'ready', groupId, resolving: false, downloadUrl: file.downloadUrl ?? undefined });
        }
      } catch {
        if (showRef.current) {
          setRun({ status: 'ready', groupId, resolving: false, resolveError: ARCHIVE_MISSING_MESSAGE });
        }
      }
    },
    [api]
  );

  // Poll the task group while it runs. One tick reads its status and its per-task summary; a finished
  // group resolves the archive, a failed one surfaces plainly, and nothing is left polling once the
  // modal closes.
  const groupId = run.status === 'running' ? run.groupId : undefined;
  useEffect(() => {
    if (!api || !groupId) {
      return;
    }
    let active = true;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const tick = async () => {
      try {
        const client = getDefaultApi(api);
        const [group, summary] = await Promise.all([
          client.taskGroupControllerItem({ id: groupId }),
          client.taskGroupControllerSummary({ id: groupId }).catch(() => undefined),
        ]);
        if (!active) {
          return;
        }
        const phase = exportPhaseFromStatus(group.status);
        if (phase === 'ready') {
          setRun({ status: 'ready', groupId, resolving: true });
          void resolveArchive(groupId, group.answerExport?.archive?.id);
          return;
        }
        if (phase === 'failed') {
          setRun({ status: 'failed', message: JOB_FAILED_MESSAGE });
          return;
        }
        setRun({ status: 'running', groupId, phase, progress: exportProgressFromSummary(summary) });
        timer = setTimeout(() => void tick(), ANSWER_EXPORT_POLL_INTERVAL_MS);
      } catch {
        if (!active) {
          return;
        }
        timer = setTimeout(() => void tick(), ANSWER_EXPORT_POLL_INTERVAL_MS);
      }
    };

    timer = setTimeout(() => void tick(), ANSWER_EXPORT_POLL_INTERVAL_MS);
    return () => {
      active = false;
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [api, groupId, resolveArchive]);

  const start = useCallback(() => {
    if (!api) {
      return;
    }
    setRun({ status: 'starting' });
    void getDefaultApi(api)
      .monitoringPromptRunControllerStartAnswerExport({ startAnswerExportDto: normalizeStartDto(dto) })
      .then((group) => {
        if (!showRef.current) {
          return;
        }
        const phase = exportPhaseFromStatus(group.status);
        if (phase === 'ready') {
          setRun({ status: 'ready', groupId: group.id, resolving: true });
          void resolveArchive(group.id, group.answerExport?.archive?.id);
          return;
        }
        if (phase === 'failed') {
          setRun({ status: 'failed', message: JOB_FAILED_MESSAGE });
          return;
        }
        setRun({ status: 'running', groupId: group.id, phase, progress: { done: 0, total: 0, percent: 0 } });
      })
      .catch(() => {
        if (showRef.current) {
          setRun({ status: 'failed', message: START_FAILED_MESSAGE });
        }
      });
  }, [api, dto, resolveArchive]);

  // The count is only usable when it was taken for exactly the filters in effect now.
  const countReady = countedKey === countRequestKey;
  const usableCount = countReady ? count : undefined;
  const hasAnswers = typeof usableCount === 'number' && usableCount > 0;
  const canStart = !!api && !running && !counting && countReady && !countError && hasAnswers;

  return (
    <EmbeddedModal className={BP_PORTAL_CLASS_NAME} show={show} onHide={onHide} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Export answers</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-4 text-sm text-slate-600">
          Export the answers that match these filters as a ZIP of CSV files — prompts, answers, answer subjects and answer subject
          attributes, each row carrying an id so the files join. The export runs in the background; the archive is offered here once it is
          ready.
        </p>

        <fieldset className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2" disabled={running}>
          <div className="flex flex-col gap-1">
            <label htmlFor="answer-export-from" className="text-sm text-slate-600">
              From
            </label>
            <input
              id="answer-export-from"
              type="date"
              value={epochMsToDateInput(dto.from)}
              onChange={(event) => patch({ from: dateInputToEpochMs(event.target.value, 'start') })}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="answer-export-to" className="text-sm text-slate-600">
              To
            </label>
            <input
              id="answer-export-to"
              type="date"
              value={epochMsToDateInput(dto.to)}
              onChange={(event) => patch({ to: dateInputToEpochMs(event.target.value, 'end') })}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="answer-export-engine" className="text-sm text-slate-600">
              Engine
            </label>
            <select
              id="answer-export-engine"
              value={singleValue(dto.engines)}
              onChange={(event) => patch({ engines: event.target.value ? [event.target.value] : null })}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
            >
              <option value="">All engines</option>
              {engineOptions.map((option) => (
                <option key={option.key} value={option.key} className={option.presentation.className}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="answer-export-locale" className="text-sm text-slate-600">
              Country / locale
            </label>
            <select
              id="answer-export-locale"
              value={singleValue(dto.countryLocales)}
              onChange={(event) => patch({ countryLocales: event.target.value ? [event.target.value] : null })}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
            >
              <option value="">All locales</option>
              {localeOptions.map((option) => (
                <option key={option.key} value={option.key} className={option.presentation.className}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1 sm:col-span-2">
            <label htmlFor="answer-export-prompt" className="text-sm text-slate-600">
              Prompt
            </label>
            <select
              id="answer-export-prompt"
              value={singleValue(dto.prompts)}
              onChange={(event) => patch({ prompts: event.target.value ? [event.target.value] : null })}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
            >
              <option value="">All prompts</option>
              {promptOptions.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.name}
                </option>
              ))}
            </select>
          </div>
        </fieldset>

        <fieldset className="mb-4 flex flex-col gap-2 text-sm text-slate-700" disabled={running}>
          <label className="mb-0 flex items-center gap-2">
            <input
              type="checkbox"
              checked={dto.includeAnswerText ?? false}
              onChange={(event) => patch({ includeAnswerText: event.target.checked })}
            />
            Include the full answer text
          </label>
          <label className="mb-0 flex items-center gap-2">
            <input
              type="checkbox"
              checked={dto.includePromptsWithoutAnswers ?? false}
              onChange={(event) => patch({ includePromptsWithoutAnswers: event.target.checked })}
            />
            Include prompts that have no answers
          </label>
          <label className="mb-0 flex items-center gap-2">
            <input
              type="checkbox"
              checked={dto.includeUntrackedSubjects ?? false}
              onChange={(event) => patch({ includeUntrackedSubjects: event.target.checked })}
            />
            Include untracked subjects
          </label>
        </fieldset>

        {countError && countReady ? (
          <div className="mb-2 text-sm text-rose-600">
            The number of answers could not be counted. The export cannot start until it can.
          </div>
        ) : null}
        {countReady && !countError && !counting && typeof usableCount === 'number' && usableCount === 0 ? (
          <div className="mb-2 text-sm text-slate-500">No answers match these filters.</div>
        ) : null}

        {run.status === 'starting' ? <div className="text-sm text-slate-500">Starting the export…</div> : null}
        {run.status === 'running' ? (
          <ProgressBar
            percent={run.progress.percent}
            title="Export progress"
            label={
              run.phase === 'queued'
                ? 'Queued…'
                : run.progress.total > 0
                  ? `Exporting answers — ${run.progress.done} of ${run.progress.total} chunks`
                  : 'Exporting answers'
            }
          />
        ) : null}
        {run.status === 'ready' ? (
          <div className="text-sm">
            {run.resolving ? (
              <span className="text-slate-500">Preparing the archive…</span>
            ) : run.downloadUrl ? (
              // The house button, not a hand-styled anchor: the anchor kept the link underline,
              // which is what made it read as a stray link rather than the action it is.
              <ButtonLink to={run.downloadUrl} target="_blank" variant="primary">
                Download archive
              </ButtonLink>
            ) : (
              <span className="text-rose-600">{run.resolveError ?? ARCHIVE_MISSING_MESSAGE}</span>
            )}
          </div>
        ) : null}
        {run.status === 'failed' ? <div className="text-sm text-rose-600">{run.message}</div> : null}
      </Modal.Body>
      <Modal.Footer>
        <div className="flex w-full items-center justify-end gap-2">
          <button
            type="button"
            onClick={onHide}
            className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-600 transition-colors duration-150 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            {run.status === 'ready' || run.status === 'failed' ? 'Close' : 'Cancel'}
          </button>
          {run.status !== 'ready' ? (
            <button type="button" disabled={!canStart} onClick={start} className={BP_PRIMARY_BUTTON_CLASSES}>
              {exportCountButtonLabel(usableCount, counting || !countReady)}
            </button>
          ) : null}
        </div>
      </Modal.Footer>
    </EmbeddedModal>
  );
}
